import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { repAPI } from '../../services/api';
import Breadcrumbs from '../../components/Breadcrumbs';

const RepManagement = () => {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    repInventoryLocation: '',
  });

  useEffect(() => {
    fetchReps();
  }, []);

  const fetchReps = async () => {
    try {
      setLoading(true);
      const response = await repAPI.getReps();
      if (response.success) {
        setReps(response.data);
      }
    } catch (err) {
      setError('Failed to load reps');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rep = null) => {
    if (rep) {
      setEditingRep(rep);
      setFormData({
        email: rep.email,
        password: '',
        firstName: rep.firstName || '',
        lastName: rep.lastName || '',
        repInventoryLocation: rep.repInventoryLocation || '',
      });
    } else {
      setEditingRep(null);
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        repInventoryLocation: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRep(null);
    setFormData({ email: '', password: '', firstName: '', lastName: '', repInventoryLocation: '' });
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (editingRep) {
        await repAPI.updateRep(editingRep.id, formData);
        setSuccess('Rep updated successfully');
      } else {
        if (!formData.password) {
          setError('Password is required for new reps');
          return;
        }
        await repAPI.createRep(formData);
        setSuccess('Rep created successfully');
      }
      fetchReps();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save rep');
      console.error(err);
    }
  };

  const handleDelete = async (repId) => {
    if (!window.confirm('Are you sure you want to delete this rep? All client relationships will be deactivated.')) return;

    try {
      setError('');
      await repAPI.deleteRep(repId);
      setSuccess('Rep deleted successfully');
      fetchReps();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete rep');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs />
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Sales Rep Management
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Manage sales representatives and their client assignments
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: 'white',
              color: '#039582',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
                color: '#027A6B'
              }
            }}
          >
            Add New Rep
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Inventory Location</TableCell>
              <TableCell>Active Clients</TableCell>
              <TableCell>Leads Created</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No reps found. Create your first rep to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              reps.map((rep) => (
                <TableRow key={rep.id}>
                  <TableCell>
                    {rep.firstName} {rep.lastName}
                  </TableCell>
                  <TableCell>{rep.email}</TableCell>
                  <TableCell>{rep.repInventoryLocation || 'Not set'}</TableCell>
                  <TableCell>
                    <Chip label={rep.activeClients || 0} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Chip label={rep.leadsCreated || 0} size="small" />
                  </TableCell>
                  <TableCell>{new Date(rep.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenDialog(rep)} size="small">
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(rep.id)} size="small" color="error">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRep ? 'Edit Rep' : 'Create New Rep'}</DialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            fullWidth
            margin="normal"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
          />
          <TextField
            label="Last Name"
            fullWidth
            margin="normal"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            helperText={editingRep ? 'Leave blank to keep current password' : 'Required for new reps'}
          />
          <TextField
            label="Inventory Location"
            fullWidth
            margin="normal"
            value={formData.repInventoryLocation}
            onChange={(e) => setFormData({ ...formData, repInventoryLocation: e.target.value })}
            helperText="e.g., Warehouse A, Van 1, Dallas Territory"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.email}>
            {editingRep ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepManagement;

